import { Button } from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { addAiComment, hasAiCommentKey } from "logic/logics";
import { aiApi, CommentType } from "api/aiApi";
import { ProgramContentType } from "data/BlockComponent";

const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="11"
    height="8"
    viewBox="0 0 11 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1 3.5L4 6.5L9.5 1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function AddCommentButton({
  taskKey,
  thoughtRecordKey,
  contentData,
  setContentData,
  setMoveToIndex,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  contentData?: ProgramContentType[];
  setContentData?: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
}) {
  const isThoughtRecord =
    taskKey.includes("2-2-T") && thoughtRecordKey !== undefined;

  const didFetchRef = useRef(false);
  const lastFetchKeyRef = useRef<string>();
  const fetchKeyRef = useRef<string>();
  const contentKeyRef = useRef<string>();
  const commentListRef = useRef<CommentType[]>([]);
  const contentDataRef = useRef(contentData);
  const isOperable = !!(contentData && setContentData && setMoveToIndex);

  useEffect(() => {
    contentDataRef.current = contentData;
    contentKeyRef.current = `${taskKey}_${thoughtRecordKey || ""}`;
    if (didFetchRef.current && fetchKeyRef.current === contentKeyRef.current) {
      handleButtonStatus(commentListRef.current);
    }
  }, [contentData, taskKey, thoughtRecordKey]);

  const [buttonStatus, setButtonStatus] = useState<
    "active" | "alreadyAdded" | "default"
  >("default");

  function handleButtonStatus(list: CommentType[]) {
    if (!isOperable) {
      setButtonStatus("default");
      return;
    }

    if (list.length === 0) {
      setButtonStatus("default");
      return;
    }

    const allKeys = list
      .flatMap((each) => each.commentList)
      .map((c) => c.aiCommentKey);

    const currentData = contentDataRef.current;

    const hasUnadded = allKeys.some((key) =>
      currentData ? !hasAiCommentKey(currentData, key) : true
    );

    if (hasUnadded) {
      setButtonStatus("active");
    } else {
      setButtonStatus("alreadyAdded");
    }
  }

  const { mutate: fetchAiCommentState } = useMutation(
    () => aiApi.getCommentList(taskKey, thoughtRecordKey),
    {
      onSuccess: (data) => {
        const list = data.result || [];
        commentListRef.current = list;
        didFetchRef.current = true;
        fetchKeyRef.current = `${taskKey}_${thoughtRecordKey || ""}`;
        if (contentKeyRef.current === fetchKeyRef.current) {
          handleButtonStatus(list);
        }
      },
      onError: (error) => {
        setButtonStatus("default");
      },
    }
  );

  const { mutate: fetchAiComments, isLoading } = useMutation(
    () => aiApi.getCommentList(taskKey, thoughtRecordKey),
    {
      onSuccess: (data) => {
        const aiCommentList = data.result || [];
        commentListRef.current = aiCommentList;
        didFetchRef.current = true;
        fetchKeyRef.current = `${taskKey}_${thoughtRecordKey || ""}`;

        if (isOperable && aiCommentList && aiCommentList.length > 0) {
          const addedIndex = addAiComment(
            isThoughtRecord,
            setContentData!,
            aiCommentList
          );
          if (addedIndex !== undefined && setMoveToIndex) {
            setMoveToIndex(addedIndex);
          }
        }
        if (contentKeyRef.current === fetchKeyRef.current) {
          handleButtonStatus(aiCommentList);
        }
      },
      onError: (error) => {
        console.error("AI Comment fetch error:", error);
      },
    }
  );

  useEffect(() => {
    const fetchKey = `${taskKey}_${thoughtRecordKey || ""}`;
    if (lastFetchKeyRef.current !== fetchKey) {
      lastFetchKeyRef.current = fetchKey;
      fetchAiCommentState();
    }
  }, [taskKey, thoughtRecordKey]);

  useEffect(() => {
    function handleDeleted(e: Event) {
      const detail = (
        e as CustomEvent<{
          taskKey: string;
          thoughtRecordKey?: string;
        }>
      ).detail;
      if (
        detail.taskKey === taskKey &&
        detail.thoughtRecordKey === thoughtRecordKey
      ) {
        fetchAiCommentState();
      }
    }
    window.addEventListener("ai_comment_deleted", handleDeleted);
    return () => {
      window.removeEventListener("ai_comment_deleted", handleDeleted);
    };
  }, [taskKey, thoughtRecordKey, fetchAiCommentState]);

  return (
    <>
      <Button
        size="sm"
        variant={buttonStatus === "alreadyAdded" ? "Outlined" : "Solid"}
        loading={isLoading}
        disabled={
          !isOperable ||
          buttonStatus === "default" ||
          buttonStatus === "alreadyAdded"
        }
        sx={{
          pointerEvents: buttonStatus === "alreadyAdded" ? "none" : "auto",
        }}
        startDecorator={
          buttonStatus === "alreadyAdded" ? <CheckIcon /> : undefined
        }
        onClick={() => {
          if (!isOperable) return;
          fetchAiComments();
        }}
      >
        {buttonStatus === "alreadyAdded" ? "질문 닮" : "질문하기"}
      </Button>
    </>
  );
}